<template>
    <div class="common-fission-list">
        <template v-if="data && data.length > 0">
            <div class="list-wrap" ref="listContainer" @scroll="handleScroll">
                <div v-for="(item, index) in data" :key="getItemKey(item, index)" class="list-item">
                    <div
                        :class="[
                            'item-content',
                            normalizeListStyleType(item) === LIST_STYLE_EXPIRED ? 'gray-item' : ''
                        ]"
                    >
                        <div class="item-content-left">
                            <div class="avatar">
                                <img :src="item.icon || defaultIcon" alt="" />
                            </div>
                            <div class="user-info-wrapper">
                                <div class="user-info">{{ getUserInfo(item) }}</div>
                                <div class="time-info" v-if="item.updateTime">
                                    {{ formatTime(item.updateTime) }}
                                </div>
                            </div>
                        </div>
                        <!-- Push 区：外层接收点击，内层 v-html，避免与 v-html 同节点时部分环境不冒泡/不触发 -->
                        <template v-if="isPushStyleRow(item)">
                            <div
                                :class="[
                                    'item-content-right',
                                    'item-content-button',
                                    isPushStyleDisabled(item) ? 'disabled' : ''
                                ]"
                                role="button"
                                @click.capture="handleClick(item)"
                            >
                                <div class="item-content-html" v-html="item.content"></div>
                            </div>
                        </template>
                        <template v-else>
                            <div class="item-content-right" v-html="item.content"></div>
                        </template>
                    </div>
                </div>
            </div>
        </template>
        <div v-else class="empty-tip" v-html="emptyContent"></div>
    </div>
</template>

<script >
import { defineEmits, defineProps, onMounted, onUnmounted, ref } from 'vue-demi';
import defaultIcon from '../assets/images/defaultIcon.png';
import { LIST_STYLE_DEFAULT, LIST_STYLE_EXPIRED, LIST_STYLE_PUSH_AVAILABLE, LIST_STYLE_PUSH_UNAVAILABLE, UID_MASK_END, UID_MASK_START } from '../assets/scripts/constants';
import { formatTime } from '../assets/scripts/utils';
const __sfc_main = {};
__sfc_main.props = {
  // 列表数据
  data: {
    type: Array,
    default: () => []
  },
  // 是否还有更多数据
  hasMore: {
    type: Boolean,
    default: false
  },
  emptyContent: {
    type: String,
    default: '暂无数据'
  }
};
__sfc_main.setup = (__props, __ctx) => {
  const props = __props;
  const emit = __ctx.emit;
  const listContainer = ref(null);
  const isLoading = ref(false); // 防止重复加载

  // 加载超时时间（毫秒）
  const LOAD_TIMEOUT = 1000;

  // 获取列表项的 key：优先邀请记录 id，否则受邀人 tarUid，最后用 index
  const getItemKey = (item, index) => {
    return item?.id ?? item?.tarUid ?? index;
  };

  // 获取用户信息显示文本
  const getUserInfo = item => {
    const {
      nick,
      tarUid
    } = item;
    if (!nick && !tarUid) {
      return '';
    }
    const uidStr = tarUid ? tarUid.toString() : '';
    if (uidStr) {
      return `${nick || ''}(${uidStr.slice(0, UID_MASK_START)}***${uidStr.slice(-UID_MASK_END)})`;
    }
    return nick || '';
  };

  /** 统一为数字，避免接口/序列化后变成字符串导致分支判断失败 */
  const normalizeListStyleType = item => {
    const v = item?.styleType;
    const n = Number(v);
    return Number.isFinite(n) ? n : LIST_STYLE_DEFAULT;
  };

  /** 是否为 Push 按钮样式（可点发提醒 / 不可点态） */
  const isPushStyleRow = item => {
    const st = normalizeListStyleType(item);
    return st === LIST_STYLE_PUSH_AVAILABLE || st === LIST_STYLE_PUSH_UNAVAILABLE;
  };

  /** 不可发 Push 时加 disabled（含 pointer-events:none，点击不会触发） */
  const isPushStyleDisabled = item => normalizeListStyleType(item) === LIST_STYLE_PUSH_UNAVAILABLE;

  // 处理滚动事件
  const handleScroll = () => {
    if (!props.hasMore || isLoading.value) {
      return;
    }
    const container = listContainer.value;
    if (!container) {
      return;
    }

    // 计算是否滚动到底部（距离底部 50px 时触发加载）
    const scrollTop = container.scrollTop;
    const scrollHeight = container.scrollHeight;
    const clientHeight = container.clientHeight;
    const threshold = 50; // 距离底部 50px 时触发

    if (scrollTop + clientHeight >= scrollHeight - threshold) {
      isLoading.value = true;
      emit('load-more', () => {
        console.log('load-more callback');
        isLoading.value = false;
      });

      // 如果父组件没有及时更新 hasMore，则在指定时间后自动重置（防止一直处于 loading 状态）
      setTimeout(() => {
        isLoading.value = false;
      }, LOAD_TIMEOUT);
    }
  };
  const handleClick = item => {
    emit('item-click', item);
  };
  onMounted(() => {
    // 组件挂载后可以执行一些初始化操作
  });
  onUnmounted(() => {
    // 清理工作
  });
  return {
    defaultIcon,
    LIST_STYLE_EXPIRED,
    formatTime,
    listContainer,
    getItemKey,
    getUserInfo,
    normalizeListStyleType,
    isPushStyleRow,
    isPushStyleDisabled,
    handleScroll,
    handleClick
  };
};
export default __sfc_main;
</script>

<style lang="less" scoped>
.common-fission-list {
    width: 100%;
    height: 100%;
    display: flex;
    -webkit-overflow-scrolling: touch; // iOS 平滑滚动

    .list-wrap {
        width: 100%;
        background-color: #fff;
        border-radius: 0.3rem;
        padding: 0.28rem 0.33rem;
        display: flex;
        flex-direction: column;
        overflow-y: auto;
    }

    .list-item {
        width: 100%;

        &:not(:last-child) {
            .item-content {
                border-bottom: 0.01rem solid #e5e5e5;
            }
        }

        .item-content {
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: 0.3rem 0;
            width: 100%;

            .item-content-left {
                flex: 1;
                min-width: 0;
                display: flex;
                align-items: center;
                margin-right: 0.2rem;
                position: relative;
                z-index: 1;

                .avatar {
                    width: 0.62rem;
                    height: 0.62rem;
                    border-radius: 50%;
                    overflow: hidden;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    background: linear-gradient(180deg, #e6e6e6 0%, #bababa 100%);
                    flex-shrink: 0;

                    img {
                        width: 100%;
                        height: 100%;
                        object-fit: cover;
                    }
                }

                .user-info-wrapper {
                    margin-left: 0.1rem;
                    display: flex;
                    flex-direction: column;
                    justify-content: center;
                    min-width: 0;

                    .user-info {
                        font-weight: 400;
                        font-size: 0.28rem;
                        color: #262626;
                        white-space: nowrap;
                        overflow: hidden;
                        text-overflow: ellipsis;
                    }

                    .time-info {
                        margin-top: 0.08rem;
                        font-weight: 400;
                        font-size: 0.22rem;
                        color: #999;
                        white-space: nowrap;
                    }
                }
            }

            .item-content-right {
                flex-shrink: 0;
                max-width: 100%;
                white-space: normal;
                word-wrap: break-word;
                word-break: break-word;
                text-align: right;
            }

            .item-content-button {
                position: relative;
                z-index: 2;
                border-radius: 0.88rem;
                border: 0.01rem solid #1d1d1d;
                padding: 0.11rem 0.22rem;
                cursor: pointer;
                box-sizing: border-box;
            }

            .item-content-html {
                display: block;
                max-width: 100%;
                min-height: inherit;
                text-align: inherit;
            }

            .disabled {
                opacity: 0.5;
                cursor: not-allowed;
            }
        }

        .gray-item {
            opacity: 0.5;
        }
    }

    .empty-tip {
        width: 100%;
        padding: 0.5rem;
        text-align: center;
        color: #999;
        font-size: 0.28rem;
        align-self: center;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;

        &::before {
            content: '';
            width: 1.5rem;
            height: 1.5rem;
            background-image: url('../assets/images/empty.png');
            background-size: contain;
            background-repeat: no-repeat;
            background-position: center;
        }
    }
}
</style>
